import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

import { Hospital } from '../../../shared/api/hscan/hospital';
import { useHospital } from '../../../shared/hooks/hospitalHook';

export interface UseAutoSearchHospitalParams {
  /**
   * 검색할 병원
   */
  hospitalId: string;
  /**
   * 병원 찾기 성공 콜백
   */
  onRequestHospitalSuccess: (hospital: Hospital) => void;
  /**
   * 검색 가능한 상황인지
   */
  isSearchable: boolean;
  /**
   * 실행할 검색 함수
   */
  search?: () => void;
}
/**
 * 처음 한번만 검색 대신해드립니다
 */
export const useAutoSearchHospital = ({
  hospitalId,
  onRequestHospitalSuccess,
  isSearchable,
  search,
}: UseAutoSearchHospitalParams) => {
  // load search term
  const queryClient = useQueryClient();

  const { refetch: requestHospital } = useHospital(
    { id: hospitalId },
    { enabled: false },
  );

  const [autoLoadEnabled, setAutoLoadEnabled] = useState(true);
  useEffect(() => {
    if (hospitalId) {
      if (autoLoadEnabled) {
        requestHospital().then(({ data: hospital }) => {
          // 정보를 가져왔는데 로딩을 취소하면 반영하지 않음
          if (hospital && autoLoadEnabled) {
            onRequestHospitalSuccess(hospital);
          }
        });
      }
    }

    return () => {
      queryClient.cancelQueries({ queryKey: ['HOSPITAL'] });
    };
  }, [
    hospitalId,
    requestHospital,
    autoLoadEnabled,
    queryClient,
    onRequestHospitalSuccess,
  ]);

  useEffect(() => {
    if (isSearchable && hospitalId) {
      // 조건이 맞는 경우 최초 한 번만 검색까지 해줌
      if (autoLoadEnabled) {
        search?.();
        setAutoLoadEnabled(false);
      }
    }
  }, [hospitalId, autoLoadEnabled, isSearchable, search]);

  return {
    abortAutoSearch() {
      setAutoLoadEnabled(false);
    },
  };
};
